"use client";
import _extends from '@babel/runtime/helpers/extends';
import { ContentText } from '../ContentText/ContentText.web.js';
import * as React from 'react';
import { ThemeProvider } from 'styled-components';
import { FileUploadAreaContainer, FileDropzoneContainer, FileUploadBorder, FileUploadContent, FileList } from './styles.js';
import { useDropzone } from 'react-dropzone';
import { FileListItem } from './FileListItem.web.js';
import { UploadStateName } from './types.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.web.js';
import { FileUploadDefaultContent } from './FileUploadDefaultContent.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { spacing } from '../constants/spacing.js';

var FileUpload = function FileUpload(props) {
  var borderColorProp = props.borderColor,
    contentMessages = props.contentMessages,
    _props$disabled = props.disabled,
    isDisabled = _props$disabled === undefined ? false : _props$disabled,
    description = props.description,
    maxFileSize = props.maxFileSize,
    minFileSize = props.minFileSize,
    _props$valid = props.valid,
    valid = _props$valid === undefined ? true : _props$valid,
    _props$errorMessage = props.errorMessage,
    errorMessage = _props$errorMessage === undefined ? 'The selected files are invalid, please review the selected files' : _props$errorMessage,
    acceptedFileTypes = props.acceptedFileTypes,
    _props$files = props.files,
    files = _props$files === undefined ? [] : _props$files,
    _props$content = props.content,
    Content = _props$content === undefined ? FileUploadDefaultContent : _props$content,
    onFilesChange = props.onFilesChange,
    onClickRemove = props.onClickRemove,
    removeIconAriaLabel = props.removeIconAriaLabel,
    width = props.width,
    height = props.height,
    fileIcon = props.fileIcon;
  var isUploading = files.some(function (file) {
    return file.state.name === UploadStateName.started;
  });
  var isInteractive = !isDisabled && !isUploading;

  /* istanbul ignore next -- coverage does not report line numbers for "some" uncovered part */
  var handleDrop = React.useCallback(function (acceptedFiles, fileRejections, event) {
    if (acceptedFiles.length + fileRejections.length > 1) {
      return;
    }
    var validFiles = acceptedFiles.filter(function (newFile) {
      return !files.some(function (existingFile) {
        return existingFile.name === newFile.name;
      });
    }).map(function (file) {
      return {
        name: file.name,
        file: file,
        state: {
          name: UploadStateName.notStarted
        }
      };
    });
    var invalidFiles = fileRejections.filter(function (rejection) {
      return !files.some(function (existingFile) {
        return existingFile.name === rejection.file.name;
      });
    }).map(function (rejection) {
      return {
        name: rejection.file.name,
        file: rejection.file,
        state: {
          name: UploadStateName.failed,
          code: rejection.errors[0].code,
          message: rejection.errors[0].message
        }
      };
    });
    // instanbul ignore next
    if (validFiles.length > 0 || invalidFiles.length > 0) {
      onFilesChange(files.concat(validFiles).concat(invalidFiles), event);
    }
  }, [files, onFilesChange]);
  var dropzoneState = useDropzone({
    disabled: isInteractive === false,
    maxFiles: 1,
    multiple: false,
    maxSize: maxFileSize,
    minSize: minFileSize,
    accept: acceptedFileTypes,
    onDrop: handleDrop
  });
  var getRootProps = dropzoneState.getRootProps,
    getInputProps = dropzoneState.getInputProps,
    isDragActive = dropzoneState.isDragActive,
    isDragAccept = dropzoneState.isDragAccept,
    isDragReject = dropzoneState.isDragReject,
    isFocused = dropzoneState.isFocused;
  var theme = useTheme();
  var borderColor = borderColorProp || theme.fileUpload.web.borderColor;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(FileUploadAreaContainer, {
    width: width
  }, /*#__PURE__*/React.createElement(FileDropzoneContainer, _extends({}, getRootProps(), {
    $disabled: isDisabled,
    $uploading: isUploading,
    $isDragActive: isDragActive,
    $isDragAccept: isDragAccept,
    $isInvalid: isDragReject,
    $isFocused: isFocused,
    $width: width,
    $height: height
  }), /*#__PURE__*/React.createElement(FileUploadBorder, {
    $borderColor: /* istanbul ignore next */
    isDragReject /* istanbul ignore next */ ? theme.colors.alert : borderColor
  }), /*#__PURE__*/React.createElement("input", _extends({}, getInputProps(), {
    "data-testid": "elemental-ui-FileUpload-input"
  })), /*#__PURE__*/React.createElement(FileUploadContent, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: width
  }, /*#__PURE__*/React.createElement(Content, {
    isDisabled: isDisabled,
    isDragActive: isDragActive,
    isDragAccept: isDragAccept,
    isDragReject: isDragReject,
    isUploading: isUploading,
    messages: contentMessages
  }))), /*#__PURE__*/React.createElement(ContentText, {
    weight: "light",
    size: "s",
    mt: spacing.xxxs,
    mb: spacing.xxs
  }, description), /*#__PURE__*/React.createElement(ErrorMessage, {
    active: !Boolean(valid)
  }, /*#__PURE__*/React.createElement(ContentText, null, errorMessage)), /*#__PURE__*/React.createElement(FileList, {
    "aria-busy": files.length === 0 ? 'true' : undefined
  }, files.map(function (uploadTarget) {
    return /*#__PURE__*/React.createElement(FileListItem, {
      key: uploadTarget.name,
      file: uploadTarget,
      disabled: isUploading,
      onClickRemove: onClickRemove,
      icon: fileIcon,
      removeIconAriaLabel: removeIconAriaLabel
    });
  }))));
};

export { FileUpload, UploadStateName };
